import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-x-2 whitespace-nowrap rounded-md font-medium text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-rw-red text-white hover:bg-rw-red/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        teal: "bg-[#377E7F] text-white hover:bg-[#2C6566]",
        submit:
          "bg-[#0F4C5C] text-white hover:bg-[#0a3a46] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500",
        eventLoungeWideActive: "bg-[#FFA600] hover:opacity-80",
        eventLoungeWideInactive:
          "border border-[#FFD699] border-dashed bg-[#FFF5E6] text-[#D9A86C] hover:opacity-80",
        eventStudyGroupOnlyActive: "bg-[#2E5F9C] text-white hover:opacity-80",
        eventStudyGroupOnlyInactive:
          "border border-[#B3C4DB] border-dashed bg-[#E9EFF7] text-[#8DA2C0] hover:opacity-80",
        formattingActive: "bg-[#0F4C5C] text-white shadow-sm hover:bg-[#0a3a46]",
        formattingInactive: "text-[#565A5B] hover:bg-gray-100 hover:text-[#0F4C5C]",
        listDefault: "text-variable-collection-dark-gray hover:bg-[#D8E0E3]",
        linkListButtons: "opacity-10 transition-opacity duration-150 hover:opacity-100",
        "rw-sub": "bg-rw-sub text-white",
        "rw-main": "bg-rw-main text-white",
        "rw-white": "bg-rw-white text-rw-black",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-5 rounded-md px-1",
        sm: "h-8 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-12 rounded-md px-10",
        icon: "h-10 w-10",
        mini: "h-5 w-5",
        event: "h-auto px-1 py-0.5 text-xs",
        formatting: "h-auto px-2 py-1 text-lg",
      },
      iconSize: {
        sm: "[&_svg]:size-3",
        default: "[&_svg]:size-4",
        lg: "[&_svg]:size-5",
        xl: "[&_svg]:size-6",
      },
      shaped: {
        true: "rounded-none rounded-tr-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      iconSize: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, iconSize, shaped, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, iconSize, shaped, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
